import type { FC, ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { FreezeIcon, ExpressIcon, DeliveryFastIcon } from '@ui';
import { observer } from 'mobx-react';
import Controls from '../../componentsNew/Controls';
import Badges from '../../componentsNew/Badges';
import ProductTask from '@routes/ProductPage/components/ProductTask';
import type { TaskItem } from '@routes/ProductPage/index.type';
import type { Badge } from '@utils/prepareBadges';
import { twcls } from '@utils/twcls';
import './index.scss';
import DesktopProductImgWrapper from './desktop';
import MobileProductImgWrapper from './mobile';
import ProductImage18Plus from '@components/ProductImage18Plus';

const BIG_MOBILE_BADGES__CLS = 'allMd:!left-[11px]';

interface ProductImgWrapperProps {
  is_18plus?: boolean;
  isConsentModalAgreed?: boolean;
  productOfDay?: boolean;
  badges: Badge[];
  isFavorite: boolean;
  prepareToAddToFavourites: () => void;
  isShowPostponed?: boolean;
  isBarMenuCurrentPath?: boolean;
  isPostponed?: boolean;
  changePostponedStatus?: () => void;
  imgsArr: string[];
  title: string;
  isDesktop?: boolean;
  isFreeze?: boolean;
  isExpressPickupAval?: boolean;
  onImgHover: () => void;
  omImgHoverEnd: () => void;
  InStoreBlockComp: ReactNode;
  productCount: number;
  isExpressDeliveryAval?: boolean;
  productNotInCart?: boolean;
  task?: TaskItem;
  isBigMobileCard?: boolean;
  isStatic?: boolean;
}

const ProductImgWrapper: FC<ProductImgWrapperProps> = observer(
  ({
    is_18plus,
    isConsentModalAgreed,
    productOfDay,
    badges,
    isFavorite,
    prepareToAddToFavourites,
    isShowPostponed,
    isBarMenuCurrentPath,
    isPostponed,
    onImgHover,
    changePostponedStatus,
    imgsArr,
    productCount,
    title,
    isDesktop,
    isFreeze,
    isExpressPickupAval,
    InStoreBlockComp,
    omImgHoverEnd,
    isExpressDeliveryAval,
    task,
    isBigMobileCard,
    isStatic
  }) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const isShowControllers = useMemo(
      () => imgsArr.length > 1,
      [imgsArr.length]
    );

    const isShowControls = useMemo(
      () => !isBarMenuCurrentPath && !isStatic,
      [isBarMenuCurrentPath, isStatic]
    );

    const productNotInCart = useMemo(
      () => productCount === undefined || productCount === 0,
      [productCount]
    );

    const content = useMemo(() => {
      if (isDesktop) {
        return (
          <DesktopProductImgWrapper
            omImgHoverEnd={omImgHoverEnd}
            isBarMenuCurrentPath={isBarMenuCurrentPath}
            imgsArr={imgsArr}
            title={title}
            InStoreBlockComp={InStoreBlockComp}
            currentSlide={currentSlide}
            setCurrentSlide={setCurrentSlide}
            productNotInCart={productNotInCart}
          />
        );
      }
      return (
        <MobileProductImgWrapper
          isShowControllers={isShowControllers}
          onImgHover={onImgHover}
          productNotInCart={productNotInCart}
          imgsArr={imgsArr}
          title={title}
          InStoreBlockComp={InStoreBlockComp}
          omImgHoverEnd={omImgHoverEnd}
          currentSlide={currentSlide}
          setCurrentSlide={setCurrentSlide}
          isBigImg={isBigMobileCard}
        />
      );
    }, [
      InStoreBlockComp,
      currentSlide,
      imgsArr,
      isBarMenuCurrentPath,
      isBigMobileCard,
      isDesktop,
      isShowControllers,
      omImgHoverEnd,
      onImgHover,
      productNotInCart,
      title
    ]);

    if (is_18plus && !isConsentModalAgreed) {
      return <ProductImage18Plus isSmallText image={imgsArr[0]} />;
    }
    return (
      <div className='relative'>
        {task && (
          <div className='absolute bottom-[22px] left-3 allMd:left-0 z-10 allMd:bottom-3'>
            <ProductTask task={task} isMobile />
          </div>
        )}
        {!productOfDay && badges?.length > 0 && (
          <Badges
            badges={badges}
            isBehaveAsLink={!isBarMenuCurrentPath && !isStatic}
            className={isBigMobileCard && BIG_MOBILE_BADGES__CLS}
          />
        )}
        {isShowControls && (
          <Controls
            isDesktop={isDesktop}
            isFavorite={isFavorite}
            prepareToAddToFavourites={prepareToAddToFavourites}
            isShowPostponed={isShowPostponed}
            isPostponed={isPostponed}
            changePostponedStatus={changePostponedStatus}
          />
        )}
        {!isBarMenuCurrentPath &&
          !isStatic &&
          (isExpressDeliveryAval || isFreeze || isExpressPickupAval) && (
            <div className='ProductImgWrapper__icons'>
              {isExpressDeliveryAval && (
                <DeliveryFastIcon
                  height={isDesktop ? 24 : 18}
                  className='ProductImgWrapper__express'
                />
              )}
              {isExpressPickupAval && (
                <ExpressIcon
                  height={isDesktop ? 24 : 18}
                  className='ProductImgWrapper__express'
                />
              )}
              {isFreeze && (
                <FreezeIcon
                  height={isDesktop ? 24 : 18}
                  className='ProductImgWrapper__freeze'
                />
              )}
            </div>
          )}
        {content}

        <div
          className={twcls(
            'ProductImgWrapper__imgs-controllers',
            !isShowControllers && 'opacity-0'
          )}
        >
          {imgsArr.length > 1 &&
            Array.from({ length: imgsArr.length }, (_, index) => (
              <div
                key={index}
                className={`ProductImgWrapper__imgs-controller${
                  currentSlide === index
                    ? ' ProductImgWrapper__imgs-controller--active'
                    : ''
                }`}
              />
            ))}
        </div>
      </div>
    );
  }
);

export default ProductImgWrapper;
